import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useSetCurrentPage } from '../stores/useAppStore';
import { useToastStore } from '../stores/useToastStore';
import GradientBackground from '../components/ui/GradientBackground';
import ParticleBackground from '../components/ui/ParticleBackground';
import SelectDropdown from '../components/SelectDropdown';
import { adminService, AIModelConfigInfo } from '../services/adminService';
import ConfirmDialog from '../components/ConfirmDialog';
import PageHeader from '../components/PageHeader';

const AIModelManagement: React.FC = () => {
  const setCurrentPage = useSetCurrentPage();
  const showToast = useToastStore((state) => state.show);
  const [loading, setLoading] = useState(true);
  const [configs, setConfigs] = useState<AIModelConfigInfo[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(20);
  const [search, setSearch] = useState('');
  const [isActiveFilter, setIsActiveFilter] = useState<boolean | undefined>(undefined);

  // 编辑/创建对话框状态
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [editingConfig, setEditingConfig] = useState<AIModelConfigInfo | null>(null);
  const [editName, setEditName] = useState('');
  const [editApiKey, setEditApiKey] = useState('');
  const [editBaseURL, setEditBaseURL] = useState('');
  const [editModel, setEditModel] = useState('');
  const [editPriority, setEditPriority] = useState(0);
  const [editIsActive, setEditIsActive] = useState(true);
  const [editDescription, setEditDescription] = useState('');
  const [saving, setSaving] = useState(false);

  // 删除确认对话框状态
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [deletingConfig, setDeletingConfig] = useState<AIModelConfigInfo | null>(null);
  const [deleting, setDeleting] = useState(false);

  // 测试对话框状态
  const [showTestDialog, setShowTestDialog] = useState(false);
  const [testingConfig, setTestingConfig] = useState<AIModelConfigInfo | null>(null);
  const [testPrompt, setTestPrompt] = useState('你好，请做个自我介绍。');
  const [testTemperature, setTestTemperature] = useState(0.7);
  const [testMaxTokens, setTestMaxTokens] = useState(1000);
  const [testEnableSearch, setTestEnableSearch] = useState(false);
  const [testResult, setTestResult] = useState<string | null>(null);
  const [testing, setTesting] = useState(false);
  const [testTaskId, setTestTaskId] = useState<string | null>(null);
  const pollTimerRef = useRef<NodeJS.Timeout | null>(null);

  // 清理轮询定时器
  useEffect(() => {
    return () => {
      if (pollTimerRef.current) {
        clearTimeout(pollTimerRef.current);
        pollTimerRef.current = null;
      }
    };
  }, []);

  // 轮询测试任务状态
  const pollTestStatus = useCallback(async (taskId: string) => {
    try {
      const status = await adminService.getTestTaskStatus(taskId);

      if (status.status === 'completed' && status.result) {
        const result = status.result as any;
        setTestResult(result.text || JSON.stringify(result, null, 2));
        setTesting(false);
        setTestTaskId(null);
        if (pollTimerRef.current) {
          clearTimeout(pollTimerRef.current);
          pollTimerRef.current = null;
        }
      } else if (status.status === 'failed') {
        setTestResult(`测试失败: ${status.error || '未知错误'}`);
        setTesting(false);
        setTestTaskId(null);
        showToast(status.error || '测试AI模型配置失败', 'error');
        if (pollTimerRef.current) {
          clearTimeout(pollTimerRef.current);
          pollTimerRef.current = null;
        }
      } else {
        // 继续轮询
        pollTimerRef.current = setTimeout(() => {
          pollTestStatus(taskId);
        }, 2000);
      }
    } catch (error: any) {
      console.error('获取测试任务状态失败:', error);
      // 网络错误等情况，继续轮询
      pollTimerRef.current = setTimeout(() => {
        pollTestStatus(taskId);
      }, 3000);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 当 testTaskId 变化时启动轮询
  useEffect(() => {
    if (testTaskId) {
      pollTestStatus(testTaskId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [testTaskId, pollTestStatus]);

  // 用于防止重复调用的 ref
  const loadingRef = useRef(false);
  // 用于搜索防抖的 ref
  const searchTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  // 用于标记是否是搜索触发的 page 变化
  const isSearchTriggeredPageChangeRef = useRef(false);

  // 加载配置列表
  const loadConfigs = async () => {
    // 防止重复调用
    if (loadingRef.current) {
      return;
    }

    try {
      loadingRef.current = true;
      setLoading(true);
      const result = await adminService.getAIModelConfigs(page, pageSize, search || undefined, isActiveFilter);
      setConfigs(result.configs);
      setTotal(result.total);
    } catch (error: any) {
      console.error('加载AI模型配置列表失败:', error);
      showToast(error.message || '加载AI模型配置列表失败', 'error');
    } finally {
      setLoading(false);
      loadingRef.current = false;
    }
  };

  // 处理搜索防抖：search 变化时，防抖后重置到第一页或加载数据
  useEffect(() => {
    // 清除之前的搜索防抖定时器
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    // 如果搜索为空，立即加载（不需要防抖）
    if (search === '') {
      isSearchTriggeredPageChangeRef.current = false;
      if (page !== 1) {
        setPage(1);
      } else {
        loadConfigs();
      }
      return;
    }

    // 搜索不为空时，防抖 500ms
    searchTimeoutRef.current = setTimeout(() => {
      // 如果搜索时不在第一页，先重置到第一页
      if (page !== 1) {
        isSearchTriggeredPageChangeRef.current = true;
        setPage(1);
      } else {
        isSearchTriggeredPageChangeRef.current = false;
        loadConfigs();
      }
    }, 500);

    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  // 当 page 或 isActiveFilter 变化时加载数据
  useEffect(() => {
    // 如果是搜索防抖触发的 page 变化，搜索防抖会调用 loadConfigs，这里跳过
    if (isSearchTriggeredPageChangeRef.current) {
      isSearchTriggeredPageChangeRef.current = false;
      return;
    }
    loadConfigs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, isActiveFilter]);

  // 打开创建对话框
  const handleCreate = () => {
    setEditingConfig(null);
    setEditName('');
    setEditApiKey('');
    setEditBaseURL('');
    setEditModel('');
    setEditPriority(0);
    setEditIsActive(true);
    setEditDescription('');
    setShowEditDialog(true);
  };

  // 打开编辑对话框
  const handleEdit = (config: AIModelConfigInfo) => {
    setEditingConfig(config);
    setEditName(config.name);
    setEditApiKey(config.apiKey);
    setEditBaseURL(config.baseURL);
    setEditModel(config.model);
    setEditPriority(config.priority);
    setEditIsActive(config.isActive);
    setEditDescription(config.description || '');
    setShowEditDialog(true);
  };

  // 保存配置
  const handleSave = async () => {
    if (!editName.trim() || !editApiKey.trim() || !editBaseURL.trim() || !editModel.trim()) {
      showToast('配置名称、API Key、Base URL和模型名称不能为空', 'error');
      return;
    }

    try {
      setSaving(true);
      if (editingConfig) {
        // 更新
        await adminService.updateAIModelConfig(editingConfig.id, {
          name: editName.trim(),
          apiKey: editApiKey.trim(),
          baseURL: editBaseURL.trim(),
          model: editModel.trim(),
          priority: editPriority,
          isActive: editIsActive,
          description: editDescription.trim() || undefined,
        });
        showToast('AI模型配置已更新', 'success');
      } else {
        // 创建
        await adminService.createAIModelConfig({
          name: editName.trim(),
          apiKey: editApiKey.trim(),
          baseURL: editBaseURL.trim(),
          model: editModel.trim(),
          priority: editPriority,
          isActive: editIsActive,
          description: editDescription.trim() || undefined,
        });
        showToast('AI模型配置已创建', 'success');
      }
      setShowEditDialog(false);
      loadConfigs();
    } catch (error: any) {
      console.error('保存AI模型配置失败:', error);
      showToast(error.message || '保存AI模型配置失败', 'error');
    } finally {
      setSaving(false);
    }
  };

  // 打开删除确认对话框
  const handleDelete = (config: AIModelConfigInfo) => {
    setDeletingConfig(config);
    setShowDeleteDialog(true);
  };

  // 确认删除
  const handleConfirmDelete = async () => {
    if (!deletingConfig) return;

    try {
      setDeleting(true);
      await adminService.deleteAIModelConfig(deletingConfig.id);
      showToast('AI模型配置已删除', 'success');
      setShowDeleteDialog(false);
      setDeletingConfig(null);
      loadConfigs();
    } catch (error: any) {
      console.error('删除AI模型配置失败:', error);
      showToast(error.message || '删除AI模型配置失败', 'error');
    } finally {
      setDeleting(false);
    }
  };

  // 打开测试对话框
  const handleOpenTest = (config: AIModelConfigInfo) => {
    setTestingConfig(config);
    setTestResult(null);
    setTestTaskId(null);
    setTesting(false);
    if (pollTimerRef.current) {
      clearTimeout(pollTimerRef.current);
      pollTimerRef.current = null;
    }
    setShowTestDialog(true);
  };

  // 运行测试
  const handleRunTest = async () => {
    if (!testingConfig || !testPrompt.trim()) return;

    try {
      setTesting(true);
      setTestResult('任务已提交，排队中...');
      const result = await adminService.testAIModelConfig({
        configId: testingConfig.id,
        prompt: testPrompt.trim(),
        temperature: testTemperature,
        maxTokens: testMaxTokens,
        enableSearch: testEnableSearch,
      });
      setTestTaskId(result.taskId);
    } catch (error: any) {
      console.error('测试AI模型配置失败:', error);
      setTestResult(`测试失败: ${error.message}`);
      showToast(error.message || '测试AI模型配置失败', 'error');
      setTesting(false);
    }
  };

  return (
    <GradientBackground>
      <div className="min-h-screen">
        <ParticleBackground />

        {/* 页面头部 */}
        <PageHeader
          title="大模型管理"
          onBack={() => setCurrentPage('personal-center')}
          rightContent={
            <button
              onClick={handleCreate}
              className="px-4 py-1.5 bg-green-500/20 text-green-300 border border-green-500/50 rounded-lg hover:bg-green-500/30 transition-colors text-sm"
            >
              新建
            </button>
          }
        />

        {/* 筛选栏 */}
        <div
          className="fixed left-0 right-0 z-40 bg-black/10 backdrop-blur-sm border-b border-white/10 px-4 py-2"
          style={{ top: '56px' }}
        >
          <div className="flex items-center gap-2">
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="搜索配置名称、模型或描述..."
              className="flex-1 bg-white/10 border border-white/20 rounded-lg px-3 py-1.5 text-white text-sm placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-white/30"
            />
            <SelectDropdown
              value={isActiveFilter === undefined ? 'all' : isActiveFilter ? 'active' : 'inactive'}
              onChange={(val) => {
                const value = val as string;
                setIsActiveFilter(value === 'all' ? undefined : value === 'active');
              }}
              options={[
                { value: 'all', label: '全部' },
                { value: 'active', label: '启用' },
                { value: 'inactive', label: '禁用' },
              ]}
              className="mb-0"
            />
          </div>
        </div>

        {/* 内容区域 */}
        <div className="px-4 pt-28 pb-4">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-white"></div>
            </div>
          ) : configs.length === 0 ? (
            <div className="text-center py-12">
              <div className="text-gray-400 mb-4">暂无AI模型配置</div>
              <button
                onClick={handleCreate}
                className="px-4 py-2 bg-green-500/20 text-green-300 border border-green-500/50 rounded-lg hover:bg-green-500/30 transition-colors"
              >
                创建第一个配置
              </button>
            </div>
          ) : (
            <>
              <div className="mt-4 mb-4 text-sm text-white/60">共 {total} 个配置</div>
              <div className="space-y-3">
                {configs.map((config) => (
                  <div key={config.id} className="bg-white/5 border border-white/20 rounded-xl p-4 text-white/90">
                    <div className="flex items-start justify-between mb-2">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="text-base font-semibold truncate">{config.name}</span>
                          {config.isActive ? (
                            <span className="px-2 py-0.5 bg-green-500/20 text-green-300 text-xs rounded border border-green-500/50 shrink-0">
                              启用
                            </span>
                          ) : (
                            <span className="px-2 py-0.5 bg-gray-500/20 text-gray-300 text-xs rounded border border-gray-500/50 shrink-0">
                              禁用
                            </span>
                          )}
                          {config.priority > 0 && (
                            <span className="px-2 py-0.5 bg-blue-500/20 text-blue-300 text-xs rounded border border-blue-500/50 shrink-0">
                              优先级: {config.priority}
                            </span>
                          )}
                        </div>
                        {config.description && (
                          <div className="text-sm text-white/60 mb-2 truncate">{config.description}</div>
                        )}
                        <div className="text-xs text-white/50 space-y-1">
                          <div className="truncate">Base URL: {config.baseURL}</div>
                          <div className="truncate">Model: {config.model}</div>
                          <div className="truncate">API Key: {config.apiKey.substring(0, 20)}...</div>
                        </div>
                      </div>
                      <div className="flex flex-col gap-2 ml-4 shrink-0">
                        <button
                          onClick={() => handleOpenTest(config)}
                          className="w-16 py-0.5 bg-green-500/20 text-green-300 border border-green-500/50 rounded-lg hover:bg-green-500/30 transition-colors text-xs"
                        >
                          测试
                        </button>
                        <button
                          onClick={() => handleEdit(config)}
                          className="w-16 py-0.5 bg-blue-500/20 text-blue-300 border border-blue-500/50 rounded-lg hover:bg-blue-500/30 transition-colors text-xs"
                        >
                          编辑
                        </button>
                        <button
                          onClick={() => handleDelete(config)}
                          className="w-16 py-0.5 bg-red-500/20 text-red-300 border border-red-500/50 rounded-lg hover:bg-red-500/30 transition-colors text-xs"
                        >
                          删除
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* 分页 */}
              {total > pageSize && (
                <div className="flex items-center justify-center gap-2 mt-6">
                  <button
                    onClick={() => setPage(page - 1)}
                    disabled={page === 1}
                    className="px-4 py-2 bg-white/10 border border-white/20 rounded-lg text-white disabled:opacity-50 disabled:cursor-not-allowed hover:bg-white/20 transition-colors"
                  >
                    上一页
                  </button>
                  <span className="text-white/60 text-sm">
                    第 {page} 页 / 共 {Math.ceil(total / pageSize)} 页
                  </span>
                  <button
                    onClick={() => setPage(page + 1)}
                    disabled={page >= Math.ceil(total / pageSize)}
                    className="px-4 py-2 bg-white/10 border border-white/20 rounded-lg text-white disabled:opacity-50 disabled:cursor-not-allowed hover:bg-white/20 transition-colors"
                  >
                    下一页
                  </button>
                </div>
              )}
            </>
          )}
        </div>

        {/* 编辑/创建对话框 */}
        {showEditDialog && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
            <div className="bg-gray-900 border border-white/20 rounded-xl p-6 w-full max-w-md mx-4 max-h-[90vh] overflow-y-auto">
              <h2 className="text-xl font-semibold text-white mb-4">
                {editingConfig ? '编辑AI模型配置' : '创建AI模型配置'}
              </h2>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm text-white/70 mb-1">配置名称 *</label>
                  <input
                    type="text"
                    value={editName}
                    onChange={(e) => setEditName(e.target.value)}
                    placeholder="如：relon、getgo、tencent_lke"
                    className="w-full bg-white/10 border border-white/20 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:ring-2 focus:ring-white/30"
                  />
                </div>

                <div>
                  <label className="block text-sm text-white/70 mb-1">API Key *</label>
                  <input
                    type="text"
                    value={editApiKey}
                    onChange={(e) => setEditApiKey(e.target.value)}
                    placeholder="输入API密钥"
                    className="w-full bg-white/10 border border-white/20 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:ring-2 focus:ring-white/30"
                  />
                </div>

                <div>
                  <label className="block text-sm text-white/70 mb-1">Base URL *</label>
                  <input
                    type="text"
                    value={editBaseURL}
                    onChange={(e) => setEditBaseURL(e.target.value)}
                    placeholder="如：https://api.relon.com"
                    className="w-full bg-white/10 border border-white/20 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:ring-2 focus:ring-white/30"
                  />
                </div>

                <div>
                  <label className="block text-sm text-white/70 mb-1">模型名称 *</label>
                  <input
                    type="text"
                    value={editModel}
                    onChange={(e) => setEditModel(e.target.value)}
                    placeholder="如：gpt-4、gemini-3-pro-preview"
                    className="w-full bg-white/10 border border-white/20 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:ring-2 focus:ring-white/30"
                  />
                </div>

                <div>
                  <label className="block text-sm text-white/70 mb-1">优先级</label>
                  <input
                    type="number"
                    value={editPriority}
                    onChange={(e) => setEditPriority(parseInt(e.target.value) || 0)}
                    placeholder="数字越大优先级越高"
                    className="w-full bg-white/10 border border-white/20 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:ring-2 focus:ring-white/30"
                  />
                </div>

                <div>
                  <label className="block text-sm text-white/70 mb-1">描述</label>
                  <textarea
                    value={editDescription}
                    onChange={(e) => setEditDescription(e.target.value)}
                    placeholder="配置描述（可选）"
                    rows={3}
                    className="w-full bg-white/10 border border-white/20 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:ring-2 focus:ring-white/30 resize-none"
                  />
                </div>

                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="editIsActive"
                    checked={editIsActive}
                    onChange={(e) => setEditIsActive(e.target.checked)}
                    className="w-4 h-4 rounded border-white/20 bg-white/10 text-green-500 focus:ring-2 focus:ring-white/30"
                  />
                  <label htmlFor="editIsActive" className="text-sm text-white/70">
                    启用此配置
                  </label>
                </div>
              </div>

              <div className="flex items-center gap-3 mt-6">
                <button
                  onClick={() => setShowEditDialog(false)}
                  className="flex-1 px-4 py-2 bg-white/10 border border-white/20 rounded-lg text-white hover:bg-white/20 transition-colors"
                >
                  取消
                </button>
                <button
                  onClick={handleSave}
                  disabled={saving}
                  className="flex-1 px-4 py-2 bg-green-500/20 border border-green-500/50 rounded-lg text-green-300 hover:bg-green-500/30 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {saving ? '保存中...' : '保存'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* 测试对话框 */}
        {showTestDialog && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
            <div className="bg-gray-900 border border-white/20 rounded-xl p-6 w-full max-w-2xl mx-4 max-h-[90vh] overflow-y-auto">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-semibold text-white">测试AI模型: {testingConfig?.name}</h2>
                <button
                  onClick={() => setShowTestDialog(false)}
                  className="text-white/50 hover:text-white transition-colors"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                <div>
                  <label className="block text-sm text-white/70 mb-1">Temperature (0-2)</label>
                  <input
                    type="number"
                    step="0.1"
                    min="0"
                    max="2"
                    value={testTemperature}
                    onChange={(e) => setTestTemperature(parseFloat(e.target.value) || 0)}
                    className="w-full bg-white/10 border border-white/20 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:ring-2 focus:ring-white/30"
                  />
                </div>
                <div>
                  <label className="block text-sm text-white/70 mb-1">Max Tokens</label>
                  <input
                    type="number"
                    step="100"
                    min="1"
                    value={testMaxTokens}
                    onChange={(e) => setTestMaxTokens(parseInt(e.target.value) || 0)}
                    className="w-full bg-white/10 border border-white/20 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:ring-2 focus:ring-white/30"
                  />
                </div>
              </div>

              <div className="flex items-center gap-2 mb-4">
                <input
                  type="checkbox"
                  id="testEnableSearch"
                  checked={testEnableSearch}
                  onChange={(e) => setTestEnableSearch(e.target.checked)}
                  className="w-4 h-4 rounded border-white/20 bg-white/10 text-green-500 focus:ring-2 focus:ring-white/30"
                />
                <label htmlFor="testEnableSearch" className="text-sm text-white/70">
                  启用联网搜索 (需模型支持)
                </label>
              </div>

              <div className="mb-4">
                <label className="block text-sm text-white/70 mb-1">测试提示词</label>
                <textarea
                  value={testPrompt}
                  onChange={(e) => setTestPrompt(e.target.value)}
                  placeholder="输入测试提示词..."
                  rows={4}
                  className="w-full bg-white/10 border border-white/20 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:ring-2 focus:ring-white/30 resize-none"
                />
              </div>

              {testResult && (
                <div className="mb-4">
                  <label className="block text-sm text-white/70 mb-1">返回结果</label>
                  <div className="w-full bg-black/30 border border-white/10 rounded-lg p-3 text-white/90 text-sm font-mono whitespace-pre-wrap max-h-60 overflow-y-auto">
                    {testResult}
                  </div>
                </div>
              )}

              <div className="flex items-center gap-3 mt-6">
                <button
                  onClick={() => setShowTestDialog(false)}
                  className="flex-1 px-4 py-2 bg-white/10 border border-white/20 rounded-lg text-white hover:bg-white/20 transition-colors"
                >
                  关闭
                </button>
                <button
                  onClick={handleRunTest}
                  disabled={testing || !testPrompt.trim()}
                  className="flex-1 px-4 py-2 bg-green-500/20 border border-green-500/50 rounded-lg text-green-300 hover:bg-green-500/30 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {testing && <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-green-300"></div>}
                  {testing ? '测试中...' : '运行测试'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* 删除确认对话框 */}
        <ConfirmDialog
          show={showDeleteDialog}
          title="删除AI模型配置"
          message={`确定要删除配置 "${deletingConfig?.name}" 吗？此操作不可恢复。`}
          confirmText="删除"
          cancelText="取消"
          onConfirm={handleConfirmDelete}
          onCancel={() => {
            setShowDeleteDialog(false);
            setDeletingConfig(null);
          }}
          confirmButtonClass="bg-red-500/20 border-red-500/50 text-red-300 hover:bg-red-500/30"
          loading={deleting}
        />
      </div>
    </GradientBackground>
  );
};

export default AIModelManagement;
